/**
 * Digit handling for machine-facing input (ZOY-314). Staff type Persian or
 * Arabic-Indic digits as naturally as Latin ones; a numeric field treats
 * them identically. Parsing stays as strict as Number() — nothing about
 * ranges, decimals or validity is loosened, only the digit alphabet widens.
 */

const FA = '۰۱۲۳۴۵۶۷۸۹';
const AR = '٠١٢٣٤٥٦٧٨٩';

export function toAsciiDigits(v: string): string {
  return v.replace(/[۰-۹٠-٩]/g, (d) => {
    const p = FA.indexOf(d);
    return String(p >= 0 ? p : AR.indexOf(d));
  });
}

/**
 * The ONE way a typed numeric field becomes a number: digit-normalized,
 * trimmed, empty and non-numeric input become null (never NaN — a NaN
 * serializes to null in JSON and silently corrupts a request body).
 */
export function parseNumericInput(raw: string): number | null {
  const v = toAsciiDigits(raw).trim();
  if (v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

/**
 * What a typed AMOUNT keeps: digits (any alphabet, normalised) and at most
 * one decimal point — separators and letters fall away. The state of a money
 * field is always this plain form; the number the request carries is
 * parseNumericInput of it.
 */
export function plainAmount(raw: string): string {
  const v = toAsciiDigits(raw).replace(/[^\d.]/g, '');
  const dot = v.indexOf('.');
  return dot === -1 ? v : `${v.slice(0, dot)}.${v.slice(dot + 1).replace(/\./g, '')}`;
}

/**
 * The same amount as reception reads it: thousands grouped ("2,500,000"),
 * a decimal part left as typed. Presentation only — never parsed back
 * without plainAmount first.
 */
export function groupAmount(plain: string): string {
  const [whole = '', frac] = plain.split('.');
  const grouped = whole.replace(/^0+(?=\d)/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return frac === undefined ? grouped : `${grouped}.${frac}`;
}
